<script setup lang="ts">
import { ref, reactive, computed, watch, nextTick, onMounted, onUnmounted } from 'vue';
import BaseForm from '@/shared/components/BaseForm.vue';
import type { RuleObject } from 'ant-design-vue/es/form';
import ButtonPrimary from '@/shared/components/button/ButtonPrimary.vue';
import type { InverterSpec } from '../models/SolshareSystemSpec';
import { useDeviceSpecificationStore } from '../stores/deviceSpecificationStore';
import { isEmptyObject } from '@/utils/object';
import {
  getInverterBrandOptions,
  isBatteryIntegrationSupported,
} from '../constants/config-constants';
import ChevronRightSVG from '@shared/assets/icon/chevron-right.svg';
import { message } from 'ant-design-vue';
import { COUNTRY_CODE } from '@/utils/constants';
import { getSolshareVersion } from '@/shared/utils/solshare-serial';
import { SOLSHARE_VERSION } from '@/shared/constants/solshare';
import { useCommissioningDeviceStore } from '../stores/commissioningDeviceStore';
import InverterSerialScanner from '../components/InverterSerialScanner.vue';
import ComModal from '../components/ComModal.vue';
import { usePollingStore } from '../stores/pollingStore';
import { PollKey } from '../models/Poll';
import type { WifiStatusResponse } from '../utils/init-pollings';
import { WIFI_STATUS_INTERVAL_MS } from '../utils/constants';
import { InfoCircleOutlined } from '@ant-design/icons-vue';
import ReadSVG from '@shared/assets/icon/read.svg';
import BaseCheckbox from '@/shared/components/BaseCheckbox.vue';

const store = useDeviceSpecificationStore();
const deviceStore = useCommissioningDeviceStore();
const pollingStore = usePollingStore();
const props = defineProps<{
  serialNumber: string;
}>();
const emit = defineEmits(['next']);
const formRef = ref();
const saving = ref(false);
const scannerOpen = ref(false);
const wifiWarningOpen = ref(false);
const wifiWarningShown = ref(false);
const editingBatteryCapacity = ref(false);

const batteryGuideUrlsByCountry = () => {
  switch (store.solshareSite?.country as COUNTRY_CODE) {
    case COUNTRY_CODE.Australia:
    case COUNTRY_CODE['New Zealand']:
      return 'https://allumeenergy.com/hubfs/au/documents/technical/0393-BatteryGuidanceforSolShareInstallations-AU.pdf';

    case COUNTRY_CODE['United Kingdom']:
    case COUNTRY_CODE.Germany:
      return null; // No battery guide for UK and Germany, UK team will provide a local guide soon.
  }

  return null; // US and other countries not supported yet
}
  
const batteryGuideUrl = batteryGuideUrlsByCountry() ?? null;
const showBatteryGuide = computed(() =>  batteryGuideUrl !== null);

let mounted = true;
const isMk2 = computed(() => getSolshareVersion(props.serialNumber) === SOLSHARE_VERSION.Mk2);

const formData = reactive<InverterSpec & { ethernetAcknowledged: boolean }>({
  systemSize: undefined,
  capacity: undefined,
  brand: undefined,
  otherBrand: undefined,
  inverterSerialNumber: undefined,
  batteryCapacity: undefined,
  ethernetAcknowledged: false,
});

const batteryIntegrationSupported = computed(
  () =>
    isMk2.value &&
    isBatteryIntegrationSupported(formData.brand, store.solshareSite?.country as COUNTRY_CODE),
);
const needsBatteryIntegration = computed(
  () => batteryIntegrationSupported.value && (formData.batteryCapacity ?? 0) > 0,
);

watch([needsBatteryIntegration, () => props.serialNumber, () => deviceStore.serialNumber], () => {
  wifiWarningOpen.value = false;
  wifiWarningShown.value = false;
});

const reportedWifi = computed(() => {
  const result = pollingStore.results[PollKey.WIFI_STATUS];
  const status = result?.data as WifiStatusResponse | undefined;
  const age = Date.now() - (result?.timestamp ?? 0);
  return (
    needsBatteryIntegration.value &&
    deviceStore.serialNumber === props.serialNumber &&
    pollingStore.polls[PollKey.WIFI_STATUS]?.running &&
    !result?.error &&
    age >= 0 &&
    age <= WIFI_STATUS_INTERVAL_MS * 2 &&
    status?.serialNumber === props.serialNumber &&
    status?.code === 200 &&
    status?.connected === true &&
    status?.connectionType === 'wifi'
  );
});

// Advisory only; acknowledgement remains the save gate. Open once when the installer confirms.
watch(reportedWifi, (wifi) => {
  if (!wifi) wifiWarningOpen.value = false;
});

watch(
  () => formData.ethernetAcknowledged,
  (acknowledged) => {
    if (!acknowledged || !reportedWifi.value || wifiWarningShown.value) return;
    wifiWarningShown.value = true;
    wifiWarningOpen.value = true;
  },
);

const onEthernetAcknowledgedChange = () => {
  formRef.value?.validateFields(['ethernetAcknowledged']).catch(() => {});
};

watch(needsBatteryIntegration, (needed) => {
  if (!needed) formData.ethernetAcknowledged = false;
});

watch(batteryIntegrationSupported, (supported) => {
  if (supported) {
    formData.batteryCapacity ??= 0;
    return;
  }
  formData.inverterSerialNumber = undefined;
  formData.batteryCapacity = undefined;
  formData.ethernetAcknowledged = false;
  scannerOpen.value = false;
  editingBatteryCapacity.value = false;
  nextTick(() => formRef.value?.clearValidate());
});

const inverterBrandOptions = computed(() => {
  return getInverterBrandOptions(store.solshareSite?.country as COUNTRY_CODE);
});

const initializeForm = () => {
  if (
    isMk2.value &&
    deviceStore.serialNumber === props.serialNumber &&
    store.inverterSpecSerial !== props.serialNumber
  ) {
    store.beginInverterSpecContext(props.serialNumber);
  }
  const spec = store.solshareSystemSpec;
  if (spec && !isEmptyObject(spec)) {
    formData.systemSize = spec.inverterSpec?.systemSize ? spec.inverterSpec.systemSize : undefined;
    formData.capacity = spec.inverterSpec?.capacity ? spec.inverterSpec.capacity : undefined;
    const owned = !isMk2.value || store.inverterSpecSerial === props.serialNumber;
    formData.brand = owned ? spec.inverterSpec?.brand || undefined : undefined;
    formData.otherBrand = owned ? spec.inverterSpec?.otherBrand || undefined : undefined;
    formData.inverterSerialNumber =
      isMk2.value && owned ? spec.inverterSpec?.inverterSerialNumber : undefined;
    formData.batteryCapacity =
      isMk2.value && owned ? spec.inverterSpec?.batteryCapacity ?? 0 : undefined;
  }
  formData.ethernetAcknowledged = false;
  scannerOpen.value = false;
  editingBatteryCapacity.value = false;
};
onMounted(initializeForm);
watch(() => props.serialNumber, initializeForm);
onUnmounted(() => {
  mounted = false;
});

const validationRules: Record<string, RuleObject[]> = {
  systemSize: [
    {
      required: true,
      message: 'DC system size is required',
      trigger: ['blur', 'change'],
    },
    {
      type: 'number',
      min: 1,
      message: 'DC system size must be a positive number',
      trigger: ['blur', 'change'],
    },
  ],
  capacity: [
    {
      required: true,
      message: 'AC inverter capacity is required',
      trigger: ['blur', 'change'],
    },
    {
      type: 'number',
      min: 1,
      message: 'AC inverter capacity must be a positive number',
      trigger: ['blur', 'change'],
    },
    {
      type: 'number',
      max: 100,
      message: 'AC inverter capacity must be 100kW or less',
      trigger: ['blur', 'change'],
    },
  ],
  brand: [
    {
      required: true,
      message: 'Inverter brand is required',
      trigger: ['blur', 'change'],
    },
  ],
  otherBrand: [
    {
      required: true,
      message: 'Inverter brand is required',
      trigger: ['blur', 'change'],
    },
  ],
  inverterSerialNumber: [
    {
      validator: (_rule, value: unknown) =>
        typeof value === 'string' && value.trim().length > 0
          ? Promise.resolve()
          : Promise.reject('Inverter serial number is required'),
      trigger: ['blur', 'change'],
    },
  ],
  batteryCapacity: [
    {
      validator: (_rule, value: unknown) =>
        typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1000
          ? Promise.resolve()
          : Promise.reject('Battery capacity must be between 0 and 1000 kWh'),
      trigger: ['blur', 'change'],
    },
  ],
  ethernetAcknowledged: [
    {
      validator: (_rule, value: unknown) =>
        value === true
          ? Promise.resolve()
          : Promise.reject('Please acknowledge the requirements'),
      trigger: 'change',
    },
  ],
};

const handleSubmit = async () => {
  if (isMk2.value) {
    if (saving.value || !mounted) return;
    const serial = props.serialNumber;
    let version = store.inverterSpecVersion;
    saving.value = true;
    scannerOpen.value = false;
    try {
      try {
        await formRef.value?.validate();
      } catch {
        return;
      }
      if (
        !mounted ||
        props.serialNumber !== serial ||
        !store.isInverterSpecContextCurrent(serial, version)
      )
        return;
      const snapshot: InverterSpec = {
        systemSize: formData.systemSize,
        capacity: formData.capacity,
        brand: formData.brand,
        otherBrand: formData.brand?.toLowerCase() === 'other' ? formData.otherBrand : undefined,
        inverterSerialNumber: batteryIntegrationSupported.value
          ? formData.inverterSerialNumber
          : undefined,
        batteryCapacity: batteryIntegrationSupported.value ? formData.batteryCapacity : undefined,
      };
      const request = store.setInverterSpec(serial, snapshot);
      version = store.inverterSpecVersion;
      const accepted = await request;
      // Successful save advances the version once more; any other change belongs to a newer context.
      if (
        accepted &&
        mounted &&
        props.serialNumber === serial &&
        store.isInverterSpecContextCurrent(serial, version + 1)
      )
        emit('next', snapshot);
    } catch (error) {
      if (
        mounted &&
        props.serialNumber === serial &&
        store.isInverterSpecContextCurrent(serial, version)
      )
        message.error((error as Error).message);
    } finally {
      saving.value = false;
    }
    return;
  }
  try {
    await formRef.value?.validate();
    if (formData.brand?.toLowerCase() !== 'other') {
      formData.otherBrand = undefined;
    }

    const {
      ethernetAcknowledged: _ack,
      inverterSerialNumber: _serial,
      batteryCapacity: _battery,
      ...legacySpec
    } = formData;
    await store.setInverterSpec(props.serialNumber, legacySpec);
    emit('next', legacySpec);
  } catch (error) {
    console.error('Validation failed:', error);
    message.error((error as Error).message);
  }
};

const onScannedSerial = (serial: string) => {
  if (!mounted || !scannerOpen.value || !batteryIntegrationSupported.value || saving.value) return;
  formData.inverterSerialNumber = serial;
  scannerOpen.value = false;
  nextTick(() => formRef.value?.validateFields(['inverterSerialNumber']).catch(() => {}));
};

// Expose validationRules & formData for unit testing
defineExpose({
  validationRules,
  formData,
});
</script>

<template>
  <div class="container">
    <BaseForm
      ref="formRef"
      :model="formData"
      :rules="validationRules"
      @finish="handleSubmit"
      id="inverter-form"
    >
      <a-form-item name="systemSize" :rules="validationRules.systemSize">
        <template #label>
          <span>DC System Size (kW)</span>
        </template>
        <a-input-number
          v-model:value="formData.systemSize"
          placeholder="Enter kW"
          :min="1"
          size="large"
          data-tn="system-size-input"
          :disabled="isMk2 && saving"
        />
      </a-form-item>
      <a-form-item name="capacity" :rules="validationRules.capacity">
        <template #label>
          <span>AC Inverter Capacity (kW)</span>
        </template>
        <a-input-number
          class="input"
          v-model:value="formData.capacity"
          placeholder="Enter kW"
          :min="1"
          size="large"
          data-tn="capacity-input"
          :disabled="isMk2 && saving"
        />
      </a-form-item>
      <a-form-item name="brand" :rules="validationRules.brand">
        <template #label>
          <span>Inverter Brand</span>
        </template>
        <a-select
          v-model:value="formData.brand"
          placeholder="Select inverter brand"
          size="large"
          data-tn="brand-input"
          :disabled="isMk2 && saving"
        >
          <a-select-option
            v-for="option in inverterBrandOptions"
            :key="option.value"
            :value="option.value"
          >
            {{ option.label }}
          </a-select-option>
        </a-select>
      </a-form-item>
      <a-form-item
        v-if="formData.brand?.toLowerCase() === 'other'"
        name="otherBrand"
        :rules="validationRules.otherBrand"
      >
        <template #label>
          <span>Other Brand</span>
        </template>
        <a-input
          v-model:value="formData.otherBrand"
          placeholder="Enter other brand"
          size="large"
          data-tn="other-brand-input"
          :disabled="isMk2 && saving"
        />
      </a-form-item>
      <template v-if="batteryIntegrationSupported">
        <a-form-item
          name="inverterSerialNumber"
          label="Inverter serial number"
          :rules="validationRules.inverterSerialNumber"
        >
          <div class="serial-input-row">
            <a-input
              v-model:value="formData.inverterSerialNumber"
              placeholder="Enter inverter serial number"
              size="large"
              :disabled="saving"
              data-tn="inverter-serial-input"
            />
            <ButtonPrimary
              html-type="button"
              :disabled="saving"
              data-tn="inverter-serial-scan"
              @click="scannerOpen = true"
              >Scan QR</ButtonPrimary
            >
          </div>
        </a-form-item>
        <a-form-item name="batteryCapacity" :rules="validationRules.batteryCapacity">
          <template #label>
            <span>
              Battery capacity (kWh) - If present
              <a-tooltip overlay-class-name="tooltip-white">
                <template #title>
                  <p class="tooltip-content">If no battery is installed, set the capacity to 0.</p>
                </template>
                <InfoCircleOutlined class="info-icon" data-tn="battery-capacity-info-icon" />
              </a-tooltip>
            </span>
          </template>
          <a-input-number
            v-model:value="formData.batteryCapacity"
            placeholder="Enter kWh (if no battery, set to 0)"
            :min="0"
            size="large"
            :disabled="saving"
            data-tn="battery-capacity-input"
            @focus="editingBatteryCapacity = true"
            @blur="editingBatteryCapacity = false"
          />
        </a-form-item>
        <template v-if="needsBatteryIntegration">
          <div class="note-container" v-if="showBatteryGuide">
            <div class="icon-container">
              <ReadSVG class="note-icon" aria-hidden="true" />
            </div>
            <div class="note-text">
              <p>
                <a
                  v-if="batteryGuideUrl"
                  :href="batteryGuideUrl ? batteryGuideUrl : undefined"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="battery-guide"
                  data-tn="battery-installation-guide"
                >
                  View the battery installation guide for more information.
                </a>
              </p>
            </div>
          </div>
          <a-form-item
            name="ethernetAcknowledged"
            :rules="validationRules.ethernetAcknowledged"
          >
            <div class="term-item term-item--first" data-tn="term-acct-ownership">
              <div>
                <p v-if="showBatteryGuide">
                  I confirm that I have reviewed the requirements and understand that an Ethernet connection is required. 
                </p>
                <p v-else>
                  I understand that connecting SolShare via Ethernet is required for future Advanced Battery Control.
                </p>
              </div>
              <BaseCheckbox
                v-model="formData.ethernetAcknowledged"
                data-tn="ethernet-acknowledgement"
                ariaLabel="Confirm that I have reviewed the requirements and understand that an Ethernet connection is required."
                @update:modelValue="onEthernetAcknowledgedChange"
              />
            </div>
          </a-form-item>
        </template>
      </template>
    </BaseForm>
    <div class="action-container">
      <ButtonPrimary
        html-type="submit"
        form="inverter-form"
        class="submit-button"
        block
        data-tn="inverter-submit"
        :loading="isMk2 && saving"
        :disabled="isMk2 && saving || (needsBatteryIntegration && !formData.ethernetAcknowledged)"
      >
        Next
        <template #iconRight>
          <ChevronRightSVG class="chevron-right-icon" />
        </template>
      </ButtonPrimary>
    </div>
    <InverterSerialScanner
      v-if="scannerOpen && batteryIntegrationSupported && !saving"
      @detect="onScannedSerial"
      @cancel="scannerOpen = false"
    />
    <ComModal
      v-if="needsBatteryIntegration && wifiWarningOpen"
      v-model:visible="wifiWarningOpen"
      title="Advanced Battery Control will require Ethernet"
      description="To save yourself a site visit when Advanced Battery Control is ready, we recommend connecting SolShare to Ethernet now."
      data-tn="ethernet-wifi-warning"
      @cancel="wifiWarningOpen = false"
    >
      <div v-if="needsBatteryIntegration && showBatteryGuide" 
          class="note-container" style="display: flex; align-items: center; justify-content: center;" >
        <div class="icon-container">
          <ReadSVG class="note-icon" aria-hidden="true" />
        </div>
        <div class="note-text">
          <p>
            <a
              v-if="batteryGuideUrl"
              :href="batteryGuideUrl ? batteryGuideUrl : undefined"
              target="_blank"
              rel="noopener noreferrer"
              class="battery-guide"
              data-tn="battery-installation-guide-modal"
            >
            View the battery installation guide for more information.
            </a>
          </p>
        </div>
      </div>
      <div class="ethernet-warning-content">
        <p>If you do not wish to connect via Ethernet today, you can still progress commissioning via Wi-Fi.</p>
      </div>
      <template #modal-buttons>
        <ButtonPrimary
          html-type="button"
          data-tn="ethernet-wifi-warning-close"
          @click="wifiWarningOpen = false"
        >
          I Acknowledge
        </ButtonPrimary>
      </template>
    </ComModal>
  </div>
</template>

<style lang="less" scoped>
.container {
  width: 100%;
}

.action-container {
  width: 100%;
  margin-top: @margin-top-end-of-page-section;
}
.ant-form-item {
  margin-bottom: 1rem;
}

.info-icon {
  color: @color-primary;
  font-size: 16px;
  margin-left: 5px;
}

.serial-input-row {
  display: flex;
  gap: 8px;

  .ant-input {
    flex: 1;
  }
}

.battery-guide {
  display: block;
  margin-bottom: 1rem;
  color: @color-primary;
  text-decoration: underline;
}

.note-container {
  display: flex;
  align-items: start;
  justify-content: center;
  gap: 1rem;
  color: @color-primary;
  padding: 1rem 0;

  .icon-container {
    flex-shrink: 0;
    .note-icon {
      width: 1.8rem;
      height: 1.8rem;
    }
  }

  .note-text {
    font-size: 1rem;
    line-height: 1.4;
    color: @color-primary;
    flex: 1 1 auto;
  }
}

.term-item {
  &:extend(.row);
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.14rem;
  border-bottom: 1px solid @color-mid-tone;
  

  &.term-item--first {
    border-top: 1px solid @color-mid-tone;
    padding: 1.57rem @content-side-margin;
    position: relative;
    left: -20px;
    width: 99vw;
  }

  p {
    margin-bottom: 0.57rem;
    &:last-of-type {
      margin-bottom: 0px;
    }
  }
}

.ethernet-warning-content {
  display: flex;
  flex-direction: column;
  padding-top: 1rem;
  gap: 1rem;
  text-align: center;
}

</style>
